import { useEffect, useState, useCallback } from 'react';
import { apiFetch, humanizeError } from '../lib/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SHOP_STORAGE_KEY = 'autonoma_shop';

/**
 * Resolve the Shopify shop domain.
 *
 *   1. ?shop=foo.myshopify.com on the current URL — Shopify always passes
 *      this when loading an embedded app inside the admin.
 *   2. localStorage fallback — covers refreshes and direct navigation
 *      after the user has used the app at least once.
 *
 * Whenever we successfully read the shop from the URL we persist it to
 * localStorage so subsequent loads can recover it even if the URL was
 * scrubbed.
 */
function getShop() {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('shop');
  if (fromUrl) {
    try { window.localStorage.setItem(SHOP_STORAGE_KEY, fromUrl); } catch {}
    return fromUrl;
  }

  try {
    return window.localStorage.getItem(SHOP_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Build a reinstall URL the UI can link to when the session is missing.
 */
function buildReinstallUrl(shop) {
  if (!shop) return `${API_URL}/api/auth/install`;
  return `${API_URL}/api/auth/install?shop=${encodeURIComponent(shop)}`;
}

// ── Shared session store ────────────────────────────────────────────────
// One module-level state that every useShopify() instance subscribes to.
// Previously each call site (20+ across the shell, pages, hooks) owned
// its own useState and fired its own /api/auth/session on mount — a cold
// load of the Ads page issued 5+ session requests before any page data,
// and instances could disagree about `authenticated` mid-flight.
let sessionState = {
  shop: getShop(),
  loading: true,
  error: null,
  authenticated: false,
};
let inFlight = null;
let fetchedOnce = false;
const listeners = new Set();

function setSessionState(next) {
  sessionState = typeof next === 'function' ? next(sessionState) : next;
  listeners.forEach((fn) => fn(sessionState));
}

/**
 * Fetch (or join the in-flight fetch of) the backend session. `force`
 * bypasses the "already fetched" short-circuit — used by refresh().
 */
function loadSession({ force = false } = {}) {
  if (inFlight) return inFlight;
  if (fetchedOnce && !force) return Promise.resolve(sessionState);

  const shop = getShop();
  setSessionState((prev) => ({ ...prev, shop, loading: true, error: null }));

  if (!shop) {
    fetchedOnce = true;
    setSessionState({ shop: null, loading: false, error: null, authenticated: false });
    return Promise.resolve(sessionState);
  }

  inFlight = (async () => {
    try {
      const res = await apiFetch('/api/auth/session', { credentials: 'include' });

      // Not signed in — surface that as a "reconnect" state, not an error.
      if (res.status === 401) {
        setSessionState({ shop, loading: false, error: null, authenticated: false });
        return sessionState;
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Session request failed (${res.status})`);
      }

      setSessionState({
        shop: data.shop || shop,
        loading: false,
        error: null,
        authenticated: !!data.authenticated,
      });
    } catch (err) {
      setSessionState({
        shop,
        loading: false,
        error: humanizeError(err, 'Unable to load session'),
        authenticated: false,
      });
    } finally {
      fetchedOnce = true;
      inFlight = null;
    }
    return sessionState;
  })();
  return inFlight;
}

/**
 * useShopify — single source of truth for the active shop.
 *
 * AUTH NOTE: authentication is the App Bridge session token attached by
 * lib/api.js to every request — the localStorage shop domain is a UI
 * convenience only (labels, reinstall links), never proof of identity.
 * The Shopify access token stays server-side and is NEVER sent here.
 *
 * Returns:
 *   {
 *     shop:          string | null,    // e.g. "foo.myshopify.com"
 *     loading:       boolean,
 *     error:         string | null,    // user-friendly error or null
 *     authenticated: boolean,          // backend confirmed the session
 *     reinstallUrl:  string,           // start install flow with this URL
 *     refresh:       () => Promise,
 *   }
 */
export function useShopify() {
  const [state, setState] = useState(sessionState);

  useEffect(() => {
    listeners.add(setState);
    // Sync in case the shared state moved between render and subscribe.
    setState(sessionState);
    loadSession();
    return () => { listeners.delete(setState); };
  }, []);

  const refresh = useCallback(() => loadSession({ force: true }), []);

  return {
    ...state,
    reinstallUrl: buildReinstallUrl(state.shop),
    refresh,
  };
}

export default useShopify;
